import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandL, BrandXXL, LabelM } from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import { CloseButton } from '@sorare/core/src/components/dialog/CloseButton/index.tsx';
import { isType } from '@sorare/core/src/lib/gql';

import gemsPackUrl from '#src/assets/gems/gems.webp';

import type { SuccessBody_purchasableItem } from './__generated__/index.graphql.ts';

const backgroundCss = css`
  --square-width: calc(var(--unit) * 5);
  background:
    linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%),
    repeating-linear-gradient(
      to left,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    repeating-linear-gradient(
      to bottom,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    linear-gradient(180deg, #284b30 0%, rgba(0, 0, 0, 0.7) 100%);
`;

const Root = styled(Vertical).attrs({ center: true })`
  text-align: center;
  gap: var(--double-unit);
  padding: var(--double-unit);
  min-height: 100%;
  ${backgroundCss}
`;

const PositionnedClose = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
  z-index: 1;
`;

const Picture = styled(Img)`
  width: calc(30 * var(--unit));
  height: calc(15 * var(--unit));
  object-fit: contain;
`;

type Props = {
  purchasableItem: SuccessBody_purchasableItem;
  onClose: () => void;
};

export const SuccessBody = ({ purchasableItem, onClose }: Props) => {
  if (isType(purchasableItem, 'InGameCurrencyPurchasableItem')) {
    const { pictureUrl, baseAmount, bonusAmount } = purchasableItem;
    return (
      <Root>
        <PositionnedClose>
          <CloseButton onClose={onClose} />
        </PositionnedClose>
        <Picture
          src={pictureUrl || gemsPackUrl}
          alt=""
          width="320"
          height="290"
        />
        <BrandXXL color="var(--c-green-200)">
          {bonusAmount + baseAmount}
        </BrandXXL>
        <BrandL>
          <FormattedMessage defaultMessage="Thank you for your purchase!" />
        </BrandL>
        <LabelM>
          <FormattedMessage defaultMessage="Use your gems in the marketplace to sign new players." />
        </LabelM>
      </Root>
    );
  }
  return null;
};

SuccessBody.fragments = {
  purchasableItem: gql`
    fragment SuccessBody_purchasableItem on PurchasableItemInterface {
      __typename
      slug
      pictureUrl
      ... on InGameCurrencyPurchasableItem {
        slug
        baseAmount
        bonusAmount
      }
    }
  ` as TypedDocumentNode<SuccessBody_purchasableItem>,
};
